import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { TiArrowSortedUp, TiArrowSortedDown } from "react-icons/ti";
import AmlUserDetailsModal from "./AmlUserDetailsModal";
import { FaRegEye } from "react-icons/fa";
import { IoCheckboxOutline } from "react-icons/io5";
import { TailSpin } from "react-loader-spinner";
import { CgMore } from "react-icons/cg";
import { TbFileDownload } from "react-icons/tb";
import axiosInstance from "../../../utils/axiosConfig";
import { useAuth } from "../../../context/auth-context";
import { API_URL } from "../../../constant";
import { toast } from "react-hot-toast";
import EmptyData from "../../../components/EmptyData";
import { jsPDF } from "jspdf";
import logoImg from "../../../assets/images/newAdminLogoLive.png";

const AmlTable = forwardRef(({ searchTerm }: any, ref) => {
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(1);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [openSettings, setOpenSettings] = useState<Record<number, boolean>>({});
  const [approvalLoader, setApprovalLoader] = useState<boolean[]>([]);
  const [currentPage, setCurrentPage] = useState<number>();
  const [totalPages, setTotalPages] = useState<number>(1);
  const [refetch, setRefetch] = useState<number>(1);
  const { isAuthenticated } = useAuth();
  const [amlLogs, setAmlLogs] = useState<any[]>([]);
  const [amlLoader, setAmlLoader] = useState(false);
  const PAGE_SIZE = 15;

  useImperativeHandle(ref, () => ({
    getData: () => amlLogs,
  }));

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "pm" : "am";

    const formattedHours = hours % 12 || 12;
    const formattedMinutes = minutes.toString().padStart(2, "0");

    return `${month} ${day}${getDaySuffix(
      day
    )}, ${year} | ${formattedHours}:${formattedMinutes}${ampm}`;
  };

  const handleDownloadPDF = (userData: any) => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    // Set dark background
    doc.setFillColor(31, 31, 31);
    doc.rect(0, 0, 210, 297, "F");

    // Watermark logo (center, faded)
    doc.addImage(logoImg, "PNG", 35, 80, 140, 40, undefined, "FAST");
    doc.setTextColor(255, 255, 255);

    // Overlay a rectangle for watermark effect (simulate opacity)
    doc.setFillColor(31, 31, 31);
    doc.setDrawColor(31, 31, 31);
    doc.rect(0, 0, 210, 297, "F");

    // Logo at the top
    doc.addImage(logoImg, "PNG", 15, 1, 40, 40, undefined, "FAST"); //make it a square

    // Use Sora font if available, else fallback
    // jsPDF does not support Sora by default, so we use a modern sans-serif
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.text("User AML Details", 60, 25);

    // User Info
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    let y = 60;
    doc.text(
      `First Name: ${userData?.userId?.profile?.firstName ?? "----"}`,
      15,
      y
    );
    y += 8;
    doc.text(
      `Last Name: ${userData?.userId?.profile?.lastName ?? "----"}`,
      15,
      y
    );
    y += 8;
    doc.text(`Email: ${userData?.userId?.email?.address ?? "----"}`, 15, y);
    y += 8;
    doc.text(`Country: ${userData?.userId?.profile?.country ?? "----"}`, 15, y);
    y += 8;
    doc.text(
      `Profile Updated At: ${
        userData?.userId?.profile?.profileUpdatedAt ?? "----"
      }`,
      15,
      y
    );
    y += 8;
    doc.text(`User ID: ${userData?.userId?._id ?? "----"}`, 15, y);
    y += 8;
    doc.text(
      `Migrated To Host Capital: ${
        userData?.userId?.migratedToHostCapital ? "Yes" : "No"
      }`,
      15,
      y
    );
    y += 8;
    doc.text(`KYC Status: ${userData?.userId?.kyc?.status ?? "----"}`, 15, y);
    y += 8;
    doc.text(
      `Push Tokens: ${userData?.userId?.pushTokens?.length ?? 0}`,
      15,
      y
    );
    y += 8;
    doc.text(`Risk Tier: ${userData?.riskTier ?? "----"}`, 15, y);
    y += 8;
    doc.text(`Risk Score: ${userData?.riskScore ?? "----"}`, 15, y);
    y += 8;
    doc.text(`PEP Match: ${userData?.pepMatch ? "Yes" : "No"}`, 15, y);
    y += 8;
    doc.text(
      `Sanctions Match: ${userData?.sanctionsMatch ? "Yes" : "No"}`,
      15,
      y
    );
    y += 8;
    doc.text(
      `High Risk Country: ${userData?.highRiskCountry ? "Yes" : "No"}`,
      15,
      y
    );
    y += 8;
    doc.text(`Country (Root): ${userData?.country ?? "----"}`, 15, y);
    y += 8;
    doc.text(`Created At: ${userData?.createdAt ?? "----"}`, 15, y);
    y += 8;
    doc.text(`Updated At: ${userData?.updatedAt ?? "----"}`, 15, y);
    y += 8;
    doc.text(`Mongo _id: ${userData?._id ?? "----"}`, 15, y);
    y += 8;
    doc.text(`__v: ${userData?.__v ?? "----"}`, 15, y);

    // KYC Status
    y += 10;
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("KYC Levels", 15, y);
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    y += 8;
    doc.text(
      `Level 1: ${userData?.kycStatus?.kyc1 ? "Completed" : "Not Completed"}`,
      20,
      y
    );
    y += 8;
    doc.text(
      `Level 2: ${userData?.kycStatus?.kyc2 ? "Completed" : "Not Completed"}`,
      20,
      y
    );
    y += 8;
    doc.text(
      `Level 3: ${userData?.kycStatus?.kyc3 ? "Completed" : "Not Completed"}`,
      20,
      y
    );

    // Footer
    doc.setFontSize(10);
    doc.setTextColor(180, 180, 180);
    doc.text("Generated with ❤️ by Deexoptions Admin", 15, 287);

    doc.save(
      `${userData?.userId?.profile?.firstName}-${userData?.userId?._id}.pdf`
    );
  };

  const getDaySuffix = (day: number) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return; // Don't fetch before the session bootstrap has confirmed one

    setAmlLoader(true);

    const fetchData = async () => {
      const searchParam = searchTerm ? `&search=${searchTerm}` : "";

      try {
        const { data } = await axiosInstance.get(
          `${API_URL}api/aml/logs?page=${page}&limit=${PAGE_SIZE}${searchParam}`
        );

        // console.log(data.data);

        const sortedAml = data.data.logs.sort(
          (a: any, b: any) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setTotalPages(data.data.pagination.totalPages);
        setCurrentPage(data.data.pagination.page);
        setAmlLogs(sortedAml);
      } catch (error: any) {
        console.log(error.response?.data?.message || "An error occurred");
        toast.error(error.response?.data?.message || "An error occurred");
      } finally {
        setAmlLoader(false);
      }
    };

    const debounceTimer = setTimeout(fetchData, 600);

    return () => clearTimeout(debounceTimer);
  }, [page, isAuthenticated, refetch, searchTerm]);

  const handleRowClick = (txn: any) => {
    setSelectedTransaction(txn);
    setShowDetailsModal(true);
  };

  const toggleModal = (objectId: number) => {
    setOpenSettings((prev) => ({
      ...prev,
      [objectId]: !prev[objectId], // Toggle the state for the specific object
    }));
  };
  const closeModal = (objectId: number) => {
    setOpenSettings((prev) => ({
      ...prev,
      [objectId]: !prev[objectId], // Close the modal for the specific object
    }));
  };
  return (
    <div className="bg-[#0D0D0D] h-[calc(100vh-400px)] pb-4 w-full font-manrope flex flex-col justify-between">
      <div className="overflow-auto ">
        <table className="w-full">
          <thead className="sticky top-0 bg-[#1C2227] z-10">
            <tr className="h-[56px] flex justify-start items-center px-4 gap-4">
              <th
                className="text-[#617889] text-[15px] w-[14.3%] flex items-center gap-1 cursor-pointer"
                onClick={() =>
                  setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                }
              >
                Log ID
                <span className="flex flex-col text-[7px] ml-1">
                  <TiArrowSortedUp
                    className={
                      sortOrder === "asc" ? "text-[#BDBDBE]" : "text-[#617889]"
                    }
                  />
                  <TiArrowSortedDown
                    className={
                      sortOrder === "desc" ? "text-[#BDBDBE]" : "text-[#617889]"
                    }
                  />
                </span>
              </th>
              <th className="text-[#617889] text-[15px] w-[14.3%] text-left">
                Risk
              </th>
              <th className="text-[#617889] text-[15px] w-[14.3%] text-left">
                Name
              </th>
              <th className="text-[#617889] text-[15px] w-[20%] text-left">
                Email
              </th>
              <th className="text-[#617889] text-[15px] w-[8.6%] text-left">
                Score
              </th>

              <th className="text-[#617889] text-[15px] w-[20%] text-left">
                Date Logged
              </th>
              <th className="text-[#617889] text-[15px] w-[8.6%] text-center">
                &nbsp;
              </th>
            </tr>
          </thead>
          {amlLoader ? (
            <tbody>
              <tr>
                <td colSpan={8}>
                  <div className="flex justify-center items-center h-40">
                    <TailSpin width={40} height={40} color="#BEE1FB" />
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {amlLogs.length === 0 ? (
                <EmptyData />
              ) : (
                amlLogs.map((txn, idx) => {
                  const approve = async () => {
                    setApprovalLoader((prev) => {
                      const updated = [...prev];
                      updated[idx] = true;
                      return updated;
                    });
                    try {
                      await axiosInstance.put(
                        `${API_URL}api/aml/logs/${txn?._id}`,
                        {}
                      );
                      toast.success("User cleared successfully");
                      setRefetch((prev: number) => prev++);
                    } catch (error: any) {
                      toast.error(
                        error.response?.data?.message || "An error occurred"
                      );
                    } finally {
                      setApprovalLoader((prev) => {
                        const updated = [...prev];
                        updated[idx] = false;
                        return updated;
                      });
                      closeModal(idx);
                    }
                  };
                  return (
                    <tr
                      key={idx}
                      className="h-[56px] flex justify-start items-center px-4 border-b gap-4 border-[#23282D] cursor-pointer hover:bg-[#181B1F]"
                    >
                      <td className="text-white w-[14.3%]  text-[14px] text-left overflow-hidden  whitespace-nowrap ">
                        {txn._id.slice(0, 4)}...{txn._id.slice(-4)}
                      </td>
                      <td className="w-[14.3%]">
                        <span
                          className={` text-[white] px-2 text-[11px] py-1 rounded-full text-xs font-bold ${
                            txn.riskTier.toLowerCase() === "high"
                              ? "bg-[#C60303]"
                              : txn.riskTier.toLowerCase() === "medium"
                              ? "bg-[#134A7C]"
                              : txn.riskTier.toLowerCase() === "low"
                              ? "bg-[#03C668]"
                              : ""
                          }`}
                        >
                          {txn.riskTier.toUpperCase()}
                        </span>
                      </td>
                      <td className="text-white w-[14.3%] flex flex-col items-start gap-1 whitespace-nowrap overflow-hidden text-ellipsis text-[14px] text-left">
                        <span>
                          {txn?.user?.profile?.firstName ?? "----"}{" "}
                          {txn?.user?.profile?.lastName ?? "----"}
                        </span>
                      </td>
                      <td className="text-white w-[20%] text-[14px] whitespace-nowrap overflow-hidden text-ellipsis text-left">
                        {txn?.user?.email?.address ?? "----"}
                      </td>
                      <td className="text-white w-[8.6%] text-[14px] text-left">
                        {txn.riskScore}
                      </td>

                      <td className="text-white w-[20%] text-[14px] text-left">
                        {formatDate(txn.createdAt)}
                      </td>
                      <td className="w-[8.6%] flex justify-center">
                        <CgMore
                          onClick={() => {
                            toggleModal(idx);
                          }}
                          className="text-[#869AA9] text-xl cursor-pointer"
                        />
                        {openSettings[idx] && (
                          <div
                            onClick={() => {
                              if (openSettings[idx]) {
                                closeModal(idx);
                              }
                            }}
                            className=" fixed inset-0 flex font-manrope justify-end pr-20 z-50  items-center bg-black bg-opacity-20  "
                          >
                            <div className="w-[190px]   bg-[#1C2227]  ">
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeModal(idx);
                                  handleRowClick(txn);
                                }}
                                className=" w-full border-b border-[#13181B] flex justify-between px-4  py-3 items-center"
                              >
                                <h4 className="text-white text-[15px]  ">
                                  View Details
                                </h4>
                                <FaRegEye className="text-white" />
                              </div>
                              <div
                                onClick={(e) => {
                                  handleDownloadPDF(txn);
                                  e.stopPropagation();
                                  closeModal(idx);
                                }}
                                className=" w-full border-b border-[#13181B] flex justify-between px-4  py-3 items-center"
                              >
                                <h4 className="text-white text-[15px]  ">
                                  Download as PDF
                                </h4>
                                <TbFileDownload className="text-white text-xl" />
                              </div>

                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  approve();
                                }}
                                className=" w-full  flex justify-between px-4  border-b border-[#13181B] py-3 items-center"
                              >
                                <h4 className="text-[#359943] text-[15px]  ">
                                  Clear
                                </h4>
                                <span>
                                  {approvalLoader[idx] ? (
                                    <TailSpin
                                      color="#ffffff"
                                      width={20}
                                      height={20}
                                    />
                                  ) : (
                                    <IoCheckboxOutline className="text-[#359943] text-[16px]" />
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          )}
        </table>
        {/* Pagination */}
      </div>
      <div className="flex justify-end items-center p-2">
        <div className="flex gap-2 items-center">
          <span className="text-[#869AA9] text-xs">
            {currentPage} of {totalPages} page{totalPages > 1 && "s"}
          </span>
          <button
            className={`w-[27px] h-[27px] rounded-lg flex justify-center items-center text-[#1C2227] border border-[#1C2227]`}
            onClick={() => {
              if (page === 1) {
                setPage(1);
              } else {
                setPage(page - 1);
              }
            }}
            disabled={page === 1}
          >
            <FiChevronLeft className="text-[#BDBDBE] text-xl" />
          </button>
          <button
            className={`w-[27px] h-[27px] rounded-lg font-bold text-[13px] flex bg-[#BEE1FB] justify-center items-center text-[#0D0D0D] ${
              page === 1
                ? "opacity-100 ext-[#0D0D0D] bg-[#BEE1FB]"
                : " bg-transparent text-white"
            }`}
            onClick={() => setPage(1)}
            disabled={page === 1}
          >
            {page}
          </button>

          <button
            className={`w-[27px] h-[27px] rounded-lg flex justify-center items-center text-[#1C2227] border border-[#1C2227]`}
            onClick={() => {
              if (page < totalPages) {
                setPage(page + 1);
              }
            }}
            disabled={page === totalPages}
          >
            <FiChevronRight className="text-[#BDBDBE] text-xl" />
          </button>
        </div>
      </div>
      <AmlUserDetailsModal
        open={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        userData={selectedTransaction}
        setRefetch={setRefetch}
      />
    </div>
  );
});

export default AmlTable;
